// El historial de evaluaciones SCI del acreditado abierto, del que sale la fila «Rechazos 30d».
// Es del CAJÓN, nunca del listado: se lee UN acreditado a la vez, el del prospecto abierto.
import { skipToken, useQuery } from '@tanstack/react-query';
import { ApiError, SessionExpiredError } from '@/api/errors';
import { listBorrowerSciEvaluations } from '@/api/services/loan-scoring';
import type { CustomerCreditFacts } from '../lib/customerCreditFacts';
import {
    borrowerRejectionsFactsOf,
    type BorrowerRejectionsFacts,
    type SciEvaluationsRead,
} from '../lib/sciRejections';

/** El `code` con el que el silo dice que el rol no alcanza. Reintentar no lo cambia. */
const PERMISSION_DENIED = 'PERMISSION_DENIED';

export interface BorrowerRejectionsSource {
    /** El prospecto ya está registrado como cliente. */
    hasCustomer: boolean;
    /** La cartera del cliente: de ahí sale el `borrowerId` al que se le pregunta. */
    credit: CustomerCreditFacts;
    /** El rol tiene `loan.scoring.read`. Sin él no se pide: el 403 sería seguro. */
    canRead: boolean;
    /** Día de calendario del piso de venta contra el que se mide la ventana. */
    today: string;
}

/** El fallo se resuelve acá dentro y viaja como dato: así la query nunca rechaza y el 403 de una
 *  lectura accesoria no dispara el toast global. */
async function readEvaluations(borrowerId: string): Promise<SciEvaluationsRead> {
    try {
        return { state: 'ready', evaluations: await listBorrowerSciEvaluations(borrowerId) };
    } catch (error) {
        // La sesión muerta SÍ se propaga: es la que el manejador global usa para mandar a login, y
        // tragársela deja la ficha ofreciendo un reintento que nunca va a funcionar.
        if (error instanceof SessionExpiredError) throw error;
        const denied = error instanceof ApiError && error.code === PERMISSION_DENIED;
        return denied ? { state: 'forbidden' } : { state: 'error' };
    }
}

/** Los rechazos recientes del acreditado del prospecto abierto, o el motivo por el que no los hay. */
export function useBorrowerRejections(source: BorrowerRejectionsSource): BorrowerRejectionsFacts {
    const borrowerId = source.credit.entry?.borrowerId ?? null;

    const query = useQuery({
        queryKey: ['loan-scoring', 'evaluations', borrowerId],
        // El id entra ya estrechado: sin acreditado no hay a quién preguntarle, y un id vacío pegaría
        // a `/scoring/borrowers//evaluations`.
        queryFn: borrowerId === null ? skipToken : () => readEvaluations(borrowerId),
        enabled: source.canRead,
        staleTime: 60_000,
    });

    return borrowerRejectionsFactsOf({
        hasCustomer: source.hasCustomer,
        credit: source.credit,
        canRead: source.canRead,
        read: query.data,
        today: source.today,
    });
}
